<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";
import { sendVoucher } from "./api";

const router = useRouter();
const csvFileName = ref(""), rows = ref([]), startDate = ref(""), uploadType = ref("bd"), loading = ref(false), success = ref(false), error = ref(""), notFound = ref([]);
const uploadTypeItems = [{ title: "BD Voucher", value: "bd" }, { title: "LSA Voucher", value: "lsa" }];

function parseCsv(text) {
  const lines = text.trim().split(/\r?\n/);
  const headers = lines[0].split(",").map(h => h.replace(/^\uFEFF/, "").trim());
  return lines.slice(1).map(line => {
    const vals = line.split(",");
    return Object.fromEntries(headers.map((h, i) => [h, (vals[i] ?? "").trim()]));
  }).filter(r => r["Employee Number"]);
}

function onFile(e) {
  const file = e.target.files?.[0]; e.target.value = "";
  if (!file) return;
  const reader = new FileReader();
  reader.onload = (ev) => { rows.value = parseCsv(ev.target.result); csvFileName.value = file.name; };
  reader.readAsText(file);
}

function sameDay(a, b) {
  const da = new Date(a), db = new Date(b);
  return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth() && da.getDate() === db.getDate();
}

async function submit() {
  error.value = ""; success.value = false; notFound.value = [];
  if (!csvFileName.value) { error.value = "Please include a CSV file."; return; }
  if (!startDate.value) { error.value = "Send Date is required."; return; }
  if (rows.value.some(r => !sameDay(startDate.value, r["BD Voucher Start Date"]))) { error.value = "Send date must match each row's voucher start date."; return; }
  loading.value = true;
  try {
    for (const r of rows.value) {
      const res = await sendVoucher({ employee_id: r["Employee Number"], formal_name: r["Formal Name"], preferred_name: r["Preferred Name"], charging_entity: r["Charging Entity"], cost_centre_code: r["Cost Centre Code"], voucher_amount: r["BD Voucher Amount"], voucher_sn: r["BD Voucher Number"], voucher_start_date: r["BD Voucher Start Date"], voucher_end_date: r["BD Voucher End Date"], batch_no: r["Batch No"], startDate: startDate.value, uploadType: uploadType.value });
      if (!res?.status) notFound.value.push({ employee_id: r["Employee Number"], formal_name: r["Formal Name"] });
    }
    success.value = true;
  } catch { error.value = "Error occurred. Please try again."; }
  finally { loading.value = false; }
}
</script>

<template>
  <section>
    <VBtn variant="text" prepend-icon="mdi:arrow-left" class="mb-4" @click="router.push({ name: 'bd-vouchers' })">Back</VBtn>
    <h1 class="text-h5 mb-4">Upload BD Vouchers</h1>
    <VCard class="pa-4" max-width="700">
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="success" type="success" class="mb-4">Upload complete.</VAlert>
      <VAlert v-if="notFound.length" type="warning" class="mb-4">
        Employees not found: {{ notFound.map(n => `${n.employee_id} (${n.formal_name})`).join(", ") }}
      </VAlert>
      <VRow>
        <VCol cols="12">
          <div class="text-body-2 font-weight-semibold mb-1">CSV File</div>
          <input type="file" accept=".csv" @change="onFile" />
          <p v-if="csvFileName" class="text-caption text-medium-emphasis mt-1">{{ csvFileName }} — {{ rows.length }} row(s)</p>
        </VCol>
        <VCol cols="12" md="6"><VTextField v-model="startDate" label="Send Date" type="datetime-local" /></VCol>
        <VCol cols="12" md="6"><VSelect v-model="uploadType" label="Upload Type" :items="uploadTypeItems" item-title="title" item-value="value" /></VCol>
        <VCol v-if="rows.length" cols="12">
          <div class="text-body-2 font-weight-semibold mb-1">Preview (first 5 rows)</div>
          <VTable density="compact">
            <thead><tr><th>Employee #</th><th>Name</th><th>Amount</th><th>Voucher #</th><th>Start</th><th>End</th></tr></thead>
            <tbody>
              <tr v-for="(r, i) in rows.slice(0, 5)" :key="i">
                <td>{{ r["Employee Number"] }}</td><td>{{ r["Formal Name"] }}</td><td>{{ r["BD Voucher Amount"] }}</td><td>{{ r["BD Voucher Number"] }}</td><td>{{ r["BD Voucher Start Date"] }}</td><td>{{ r["BD Voucher End Date"] }}</td>
              </tr>
            </tbody>
          </VTable>
        </VCol>
      </VRow>
      <VBtn :loading="loading" :disabled="!csvFileName" class="mt-4" @click="submit">Submit</VBtn>
    </VCard>
  </section>
</template>
